import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SalonHeaderActions from '../components/SalonHeaderActions';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { clearAllGuestKioskState } from '../../face-identity/utils/faceIdentityApi';
import { logoutFranchiseFlow } from '../../shared/franchiseFlowAuth';
import {
  SALON_HUB_PATH,
  SALON_SERVICE_LOBBY_PATH,
  resolveSalonHomePath,
} from '../utils/salonHomeNav';
import { isSalonDeskMode } from '../utils/salonKioskMode';
import {
  readVisitHandoff,
  buildAnalysisNavState,
  resolveAndRestoreActiveVisit,
} from '../utils/visitHandoff';
import { fetchActiveVisit } from '../../salon-admin/utils/checkinQueueApi';
import { isFaceCheckedIn, readFaceSalonProfile } from '../utils/bootstrapFaceSalonSession';
import {
  clearSalonFaceSession,
  getActiveCheckinSession,
} from '../utils/salonFaceSession';
import { clearVisitHandoff, finalizeOpenVisitOnLogout } from '../utils/visitHandoff';
import { hairAppPath } from '../../../hair-analyzer/src/hair/config/hairApp';
import {
  BEAUTY_PRODUCTS,
  expandEntitledBeautyModules,
  isBeautyModuleEntitled,
  SAAS_MODULE_IDS,
} from '../../../beauty-shared/config/saasModules';
import { useSkinSession } from '../context/SkinSessionContext';
import { bootstrapFaceSalonToCapture } from '../utils/bootstrapFaceSalonSession';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { clearSession } from '../utils/sessionManager';
import { SHELL_MQ } from '../../shared/tabletMedia';
import { resetNaturalsSalonLuxScroll } from '../../shared/resetEnterpriseSalonLuxScroll';
import TermsAndConditionsModal from '../components/TermsAndConditionsModal';
import { useKioskLandingConfig } from '../../face-identity/hooks/useKioskLandingConfig';
import {
  KIOSK_PAGE_KEYS,
  buildSizingStyle,
  isSectionVisible,
  normalizeTermsAndConditions,
} from '../../face-identity/utils/kioskLandingDefaults';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import '../components/InstructionsModal.css';
import {
  clearPresentationDemoGuestState,
  clearPresentationMode,
} from '../../presentation/presentationSession';
import './FlowSelectPage.css';

const LeafIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3c.4 3.2 2.2 5.4 4.8 6.2C14.2 10 12.4 12.2 12 15.4 11.6 12.2 9.8 10 7.2 9.2 9.8 8.4 11.6 6.2 12 3z" />
  </svg>
);

const BackIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const STORAGE_KEY = 'salon_entitled_beauty_modules';

function readEntitledModules() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

/** Production `/naturals/salon/hub`: skin + hair analysis. Test studios: `/naturals/salon/test`. */
const FLOW_OPTIONS = [
  {
    id: 'salon',
    title: 'Skin Analysis',
    iconSrc: '/hub/skin.png',
    action: 'salon-bootstrap',
    always: true,
  },
  {
    id: 'hair-analyze',
    moduleId: SAAS_MODULE_IDS.HAIR_ANALYZE,
    title: 'Hair Analysis',
    iconSrc: '/hub/hair.png',
    path: hairAppPath('/'),
    always: true,
  },
  {
    id: 'makeup-studio',
    moduleId: BEAUTY_PRODUCTS.makeupStudioTest.moduleId,
    title: BEAUTY_PRODUCTS.makeupStudioTest.title,
    iconSrc: '/hub/makeup.png',
    path: `${BEAUTY_PRODUCTS.makeupStudioTest.naturalsAppBase}/capture`,
    testOnly: true,
  },
  {
    id: 'hair-coloring',
    moduleId: BEAUTY_PRODUCTS.hairColoring.moduleId,
    title: BEAUTY_PRODUCTS.hairColoring.title,
    iconSrc: '/hub/color.png',
    path: `${BEAUTY_PRODUCTS.hairColoring.naturalsAppBase}/capture`,
    testOnly: true,
  },
  {
    id: 'hair-styling-pro',
    moduleId: BEAUTY_PRODUCTS.hairStyling.moduleId,
    title: BEAUTY_PRODUCTS.hairStyling.title,
    iconSrc: '/hub/hairstyle.png',
    path: `${BEAUTY_PRODUCTS.hairStyling.naturalsAppBase}/capture`,
    testOnly: true,
  },
  {
    id: 'hair-flux-prompt',
    moduleId: BEAUTY_PRODUCTS.hairFluxPrompt.moduleId,
    title: BEAUTY_PRODUCTS.hairFluxPrompt.title,
    iconSrc: '/hub/hairstyle.png',
    path: BEAUTY_PRODUCTS.hairFluxPrompt.naturalsAppBase,
    testOnly: true,
  },
];

export default function FlowSelectPage({ testMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSkinSession();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const { config: landingConfig } = useKioskLandingConfig(KIOSK_PAGE_KEYS.SALON_ENTRY);
  const terms = useMemo(
    () => normalizeTermsAndConditions(landingConfig?.termsAndConditions),
    [landingConfig?.termsAndConditions],
  );
  const brand = landingConfig?.brand || {};
  const consent = landingConfig?.consent || {};
  const hubCopy = useMemo(() => {
    if (testMode) return { title: 'Test studios' };
    const title = String(landingConfig?.title || '').trim() || 'Choose an experience';
    return { title };
  }, [landingConfig, testMode]);
  const hubThemeStyle = useMemo(
    () => buildSizingStyle(landingConfig?.sizing || {}),
    [landingConfig?.sizing],
  );
  const showBrand = isSectionVisible(landingConfig, 'brand');
  const showBrandLogo = showBrand && brand.showLogo !== false;
  const showTitle = isSectionVisible(landingConfig, 'title');
  const [salonBusy, setSalonBusy] = useState(false);
  const [salonError, setSalonError] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [disclaimerModal, setDisclaimerModal] = useState('');
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [entitledRaw] = useState(() => readEntitledModules());
  const pageRef = useRef(null);

  const flowOptions = useMemo(() => {
    const entitledSet = entitledRaw
      ? expandEntitledBeautyModules(entitledRaw)
      : null;
    return FLOW_OPTIONS.filter((option) => {
      if (option.disabled) return false;
      if (Boolean(option.testOnly) !== testMode) return false;
      if (option.always || testMode) return true;
      return isBeautyModuleEntitled(entitledSet, option.moduleId);
    });
  }, [entitledRaw, testMode]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const mq = window.matchMedia(SHELL_MQ);

    const clearScrollMode = () => {
      html.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
      body.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
    };

    const applyScrollMode = () => {
      if (mq.matches) {
        html.classList.add('salon-hub-scroll', 'naturals-touch-shell');
        body.classList.add('salon-hub-scroll', 'naturals-touch-shell');
      } else {
        clearScrollMode();
      }
    };

    applyScrollMode();
    mq.addEventListener('change', applyScrollMode);

    return () => {
      mq.removeEventListener('change', applyScrollMode);
      clearScrollMode();
    };
  }, []);

  // Real tablet scroller is `.naturals-salon-lux__content` (shared footer shell),
  // not this page root — NaturalsKioskTabletFooter.css forces page overflow:visible.
  useEffect(() => {
    const prevRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const resetToTop = () => resetNaturalsSalonLuxScroll(pageRef.current?.parentElement);

    resetToTop();
    const raf = requestAnimationFrame(resetToTop);
    const t0 = window.setTimeout(resetToTop, 0);
    const t1 = window.setTimeout(resetToTop, 120);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = prevRestoration || 'auto';
      }
    };
  }, [location.key]);

  useEffect(() => {
    let cancelled = false;
    const apiBase = getApiBaseUrl();
    const url = apiBase
      ? `${apiBase.replace(/\/$/, '')}${SALON_API_PREFIX}/config/disclaimer`
      : `${SALON_API_PREFIX}/config/disclaimer`;
    fetchWithTimeout(url, { timeoutMs: 15000 })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled && data && typeof data.disclaimer_modal === 'string') {
          setDisclaimerModal(data.disclaimer_modal.trim());
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setConsentChecked(false);
  }, [location.key]);

  // Live hub must not inherit presentation demo guest or sticky presentation session.
  useEffect(() => {
    if (testMode) return;
    clearPresentationMode();
    clearPresentationDemoGuestState();
  }, [testMode, location.key]);

  // Rehydrate open visit after soft reload so hub tiles do not bounce to Face Identity scan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await resolveAndRestoreActiveVisit();
      } catch {
        /* ignore — tile click will retry */
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [location.key]);

  const resetGuestForNewVisit = () => {
    clearSalonFaceSession();
    clearVisitHandoff();
    clearAllGuestKioskState();
    clearSession();
    setConsentChecked(false);
    setSalonError('');
    setSalonBusy(false);
  };

  const handleHomeToLanding = () => {
    // Always return to salon home (service lobby). Never open Face Identity / guest-intent.
    if (!isSalonDeskMode()) {
      resetGuestForNewVisit();
    }
    navigate(resolveSalonHomePath(), { replace: true, state: {} });
  };

  const handleBack = useCallback(() => {
    if (Boolean(location.state?.fromStylistDesk) || isSalonDeskMode()) {
      navigate(SALON_SERVICE_LOBBY_PATH, { replace: false, state: location.state || {} });
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/naturals/salon/welcome', { replace: false, state: location.state || {} });
  }, [navigate, location.state]);

  const handleLogout = async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    try {
      if (!isSalonDeskMode()) {
        await finalizeOpenVisitOnLogout({ complete: false });
        resetGuestForNewVisit();
      }
      await logoutFranchiseFlow();
    } finally {
      navigate('/naturals/salon/login', { replace: true, state: {} });
      setLogoutBusy(false);
    }
  };

  const handleSalonContinue = async () => {
    if (salonBusy) return;
    const handoff = readVisitHandoff();
    const active = getActiveCheckinSession();
    let checkinId =
      location.state?.checkinId ||
      handoff?.checkinId ||
      active?.id ||
      null;
    let analysisState = {
      ...(location.state || {}),
      checkinId: checkinId || location.state?.checkinId || null,
      customerName: location.state?.customerName || handoff?.customerName || '',
      faceUserId:
        location.state?.faceUserId || handoff?.faceUserId || active?.faceUserId || '',
      fromStylistDesk: Boolean(location.state?.fromStylistDesk || isSalonDeskMode()),
    };
    if (checkinId) {
      try {
        const visit = await fetchActiveVisit({ checkinId });
        if (visit?.id) {
          analysisState = buildAnalysisNavState(visit, {
            ...analysisState,
            token: token || location.state?.token || null,
            deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
            fromStylistDesk: analysisState.fromStylistDesk,
          });
          checkinId = visit.id;
        }
      } catch {
        /* continue with profile bootstrap */
      }
    } else {
      try {
        const restored = await resolveAndRestoreActiveVisit();
        if (restored?.id) {
          checkinId = restored.id;
          const visit = await fetchActiveVisit({ checkinId });
          if (visit?.id) {
            analysisState = buildAnalysisNavState(visit, {
              ...analysisState,
              token: token || location.state?.token || null,
              deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
              fromStylistDesk: true,
            });
          }
        }
      } catch {
        /* walk-in without visit may still need scan */
      }
    }

    // Open visit / desk guest already checked in — never re-open Face Identity scan.
    const hasOpenVisit = Boolean(
      checkinId ||
        getActiveCheckinSession()?.id ||
        readVisitHandoff()?.checkinId ||
        analysisState.fromStylistDesk,
    );
    if (!isFaceCheckedIn(analysisState) && !hasOpenVisit) {
      navigate('/naturals/salon/scan', {
        replace: false,
        state: {
          next: SALON_HUB_PATH,
          mode: 'salon',
          checkinId,
          token: token || location.state?.token || null,
          deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
          customerName: analysisState.customerName || handoff?.customerName || '',
          mobile: analysisState.mobile || handoff?.mobile || '',
          faceUserId: analysisState.faceUserId || handoff?.faceUserId || '',
        },
      });
      return;
    }
    setSalonBusy(true);
    setSalonError('');
    try {
      await bootstrapFaceSalonToCapture(navigate, {
        token: token || location.state?.token || null,
        deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
        locationState: analysisState,
        replace: false,
      });
    } catch (err) {
      setSalonError(err?.message || 'Could not start salon session. Try again.');
      setSalonBusy(false);
    }
  };

  const guestLabel =
    location.state?.customerName ||
    readFaceSalonProfile(location.state).name ||
    readVisitHandoff()?.customerName ||
    '';

  const navigateToAnalysisModule = (path) => {
    const handoff = readVisitHandoff();
    const profile = readFaceSalonProfile(location.state);
    navigate(path, {
      state: {
        ...(location.state || {}),
        customerName: location.state?.customerName || profile.name || handoff?.customerName || '',
        mobile: location.state?.mobile || profile.mobile || handoff?.mobile || '',
        email: location.state?.email || profile.email || handoff?.email || '',
        gender: location.state?.gender || profile.gender || handoff?.gender || '',
        age: location.state?.age ?? profile.age ?? handoff?.age,
        dob: location.state?.dob || profile.dateOfBirth || handoff?.dob || '',
        faceUserId: location.state?.faceUserId || profile.faceUserId || handoff?.faceUserId || '',
        checkinId: location.state?.checkinId || handoff?.checkinId || null,
        faceProfile: location.state?.faceProfile || undefined,
        fromStylistDesk: Boolean(location.state?.fromStylistDesk),
      },
    });
  };

  return (
    <div
      ref={pageRef}
      className="salon-flow-select-page page-bg salon-hub-has-inline-logout"
      style={hubThemeStyle}
    >
      <header className="salon-flow-select-header">
        <button
          type="button"
          className="salon-flow-select-icon-btn salon-flow-select-header-back"
          onClick={handleBack}
          aria-label="Go back"
          title="Back"
        >
          <BackIcon />
        </button>
        <div className="salon-flow-select-header-actions">
          <button
            type="button"
            className="salon-flow-select-icon-btn"
            onClick={() => window.location.reload()}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshIcon />
          </button>
          <SalonHeaderActions
            homeButtonClassName="salon-flow-select-icon-btn"
            logoutButtonClassName="salon-flow-select-icon-btn"
            onHome={handleHomeToLanding}
            onLogout={handleLogout}
            logoutBusy={logoutBusy}
          />
        </div>
      </header>

      <main className="salon-flow-select-main">
        <div className="salon-flow-select-brand">
          {showBrandLogo ? (
            <FaceIdentityBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              imgClassName="salon-flow-select-logo"
              fallbackClassName="salon-flow-select-logo-text"
            />
          ) : null}
        </div>

        <div className="salon-flow-select-leaf" aria-hidden="true">
          <LeafIcon />
        </div>

        {showTitle ? <h1 className="salon-flow-select-title">{hubCopy.title}</h1> : null}
        {guestLabel ? (
          <p className="salon-flow-select-kicker" style={{ marginTop: '0.35rem' }}>
            {guestLabel}
          </p>
        ) : null}

        <section className="salon-flow-select-consent" aria-labelledby="salon-hub-consent-title">
          <div className="instructions-disclaimer">
            <div className="disclaimer-checkbox-container">
              <label className="disclaimer-checkbox-label salon-flow-select-consent-label">
                <input
                  type="checkbox"
                  className="disclaimer-checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <span className="disclaimer-checkbox-text">
                  I accept the{' '}
                  <button
                    type="button"
                    className="disclaimer-link-button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                  >
                    {consent.termsLinkLabel || 'Terms & Disclaimer'}
                  </button>
                </span>
              </label>
            </div>
          </div>
        </section>

        {salonError ? (
          <p className="salon-flow-select-subtitle salon-flow-select-error" role="alert">
            {salonError}
          </p>
        ) : null}

        <div className="salon-flow-select-grid" role="list">
          {flowOptions.map((option) => {
            const isDisabled =
              Boolean(option.disabled) ||
              !consentChecked ||
              (option.action === 'salon-bootstrap' && salonBusy);
            return (
              <button
                key={option.id}
                type="button"
                className={`salon-flow-select-card salon-flow-select-card--simple${isDisabled ? ' is-disabled' : ''}`}
                onClick={() => {
                  if (isDisabled) return;
                  // Skin Analysis may need Face Identity / salon bootstrap.
                  if (option.action === 'salon-bootstrap') {
                    handleSalonContinue();
                    return;
                  }
                  // Makeup Studio, Hair Analyze, and Hair Style have their own capture —
                  // never open Face Identity scan from these hub tiles.
                  if (option.path) {
                    navigateToAnalysisModule(option.path);
                  }
                }}
                disabled={isDisabled}
              >
                <span className="salon-flow-select-card-icon" aria-hidden="true">
                  <img
                    src={option.iconSrc}
                    alt=""
                    className="salon-flow-select-card-icon-img"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span className="salon-flow-select-card-title">
                  {option.action === 'salon-bootstrap' && salonBusy ? 'Starting…' : option.title}
                  {option.disabled ? (
                    <span className="salon-flow-select-card-badge">Soon</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </main>

      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => setConsentChecked(true)}
        disclaimerText={disclaimerModal || undefined}
        title={terms.title}
        bodyHtml={terms.body}
      />
    </div>
  );
}
